/*
  Centralized PropTypes
  Read more here: https://medium.freecodecamp.org/react-pattern-centralized-proptypes-f981ff672f3b
*/

import PropTypes from 'prop-types';
import { status } from 'context/EditorContext'

const {
  shape,
  string,
  func,
  number,
  oneOfType,
  arrayOf,
  bool,
} = PropTypes;

const pageCardShape = shape({
  height: string.isRequired,
  width: string.isRequired,
});

const headerShape = shape({
  color: string.isRequired,
});

export const themeType = shape({
  colors: shape({
    base: string.isRequired,
    baseInverse: string.isRequired,
    baseLighter: string.isRequired,
    bg: string.isRequired,
    bgAnimation: string.isRequired,
    bgDark: string.isRequired,
    bgFocus: string.isRequired,
    bgLighter: string.isRequired,
    bgLighterS: string.isRequired,
    bgPrimary: string.isRequired,
    black: string.isRequired,
    blackLight: string.isRequired,
    blue: string.isRequired,
    blueFocus: string.isRequired,
    blueLight: string.isRequired,
    border: string.isRequired,
    borderBtn: string.isRequired,
    borderLighter: string.isRequired,
    danger: string.isRequired,
    dangerDarker: string.isRequired,
    dangerLighter: string.isRequired,
    editorBg: string.isRequired,
    editorHighlight: string.isRequired,
    green: string.isRequired,
    grey: string.isRequired,
    greyDark: string.isRequired,
    greyLighS: string.isRequired,
    greyLight: string.isRequired,
    greyLightM: string.isRequired,
    greyLighter: string.isRequired,
    orange: string.isRequired,
    primary: string.isRequired,
    primaryDarker: string.isRequired,
    primaryLighter: string.isRequired,
    red: string.isRequired,
    redDarker: string.isRequired,
    redLighter: string.isRequired,
    warn: string.isRequired,
    warnDark: string.isRequired,
    warnLighter: string.isRequired,
    white: string.isRequired,
    whiteDarker: string.isRequired,
    yellow: string.isRequired,
  }).isRequired,
  components: shape({
    button: shape({
      fontSize: string.isRequired,
      fontWeight: string.isRequired,
    }).isRequired,
    form: shape({
      maxWidth: string.isRequired,
    }).isRequired,
    layout: shape({
      backgroundColor: string.isRequired,
      paddingHorizontal: string.isRequired,
      paddingVertical: string.isRequired,
      widthNav: string.isRequired,
    }).isRequired,
    menuHeader: shape({
      width: string.isRequired,
    }).isRequired,
    navActions: shape({
      backgroundColor: string.isRequired,
      width: string.isRequired,
    }).isRequired,
    navGlobal: shape({
      paddingRight: string.isRequired,
      width: string.isRequired,
      widthClosed: string.isRequired,
    }).isRequired,
    pageCard: pageCardShape.isRequired,
    sectionCard: pageCardShape.isRequired,
    table: shape({
      header: headerShape.isRequired,
    }).isRequired,
    text: headerShape.isRequired,
  }).isRequired,
  shadows: shape({
    base: string.isRequired,
    button: string.isRequired,
    card: string.isRequired,
    icon: string.isRequired,
  }).isRequired,
  sizes: shape({
    l: string.isRequired,
    m: string.isRequired,
    s: string.isRequired,
    xl: string.isRequired,
    xl2: string.isRequired,
    xl3: string.isRequired,
    xl4: string.isRequired,
    xl5: string.isRequired,
    xl6: string.isRequired,
    xl7: string.isRequired,
    xs: string.isRequired,
    xs2: string.isRequired,
    xs3: string.isRequired,
  }).isRequired,
  spacing: shape({
    l: string.isRequired,
    m: string.isRequired,
    s: string.isRequired,
    sm: string.isRequired,
    xl: string.isRequired,
    xl1: string.isRequired,
    xl2: string.isRequired,
    xl3: string.isRequired,
    xl4: string.isRequired,
    xl5: string.isRequired,
    xl6: string.isRequired,
    xs: string.isRequired,
    xs2: string.isRequired,
    xs3: string.isRequired,
    xs4: string.isRequired,
  }).isRequired,
  type: shape({
    base: string.isRequired,
    icons: string.isRequired,
    serif: string.isRequired,
  }).isRequired,
  typos: shape({
    button: string.isRequired,
    input: string.isRequired,
    link: string.isRequired,
    text: string.isRequired,
    title: string.isRequired,
  }).isRequired,
  values: shape({
    borderRadius: string.isRequired,
    borderRadiusM: string.isRequired,
    borderSize: string.isRequired,
    headerLine: pageCardShape.isRequired,
    zIndex: shape({
      m: string.isRequired,
      s: string.isRequired,
      superBig: string.isRequired,
      l: string.isRequired,
    }).isRequired,
  }).isRequired,
  weight: shape({
    base: number.isRequired,
    bold: number.isRequired,
    light: number.isRequired,
    xbase: number.isRequired,
    xlight: number.isRequired,
    xxlight: number.isRequired,
  }).isRequired,
});

export const iconType = string;

export const historyType = shape({
  location: shape({ pathname: string }),
  push: func,
});

export const locationType = shape({
  hash: string,
  key: string,
  pathname: string,
  search: string,
});

export const errorType = shape({
  message: string,
  code: oneOfType([number, string])
});

export const modifiersType = oneOfType([string, arrayOf(string)]);

export const sitesType = arrayOf(
  shape({
    slug: string.isRequired,
    initials: string.isRequired,
    description: string.isRequired,
    color: string.isRequired,
    isActive: bool,
  })
);

export const userType = shape({
  name: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
});

export const imageType = PropTypes.shape({
  src: PropTypes.string,
  alt: PropTypes.string,
});

export const mediaType = PropTypes.shape({
  title: PropTypes.string,
  alt: PropTypes.string,
  url: PropTypes.string,
  isLocal: PropTypes.bool,
})

export const slideType = PropTypes.shape({
  description: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  image: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    alt: PropTypes.string,
    title: PropTypes.string,
    url: PropTypes.string,
  }),
  summary: PropTypes.string,
  title: PropTypes.string,
  url: PropTypes.string,
});

export const screenType = PropTypes.oneOf(['mobile', 'tablet', 'desktop']);

export const refType = PropTypes.oneOfType([
  PropTypes.func,
  PropTypes.shape({ current: PropTypes.any })
])

export const richTextEditorOptions = PropTypes.shape({
  activeColor: PropTypes.string,
})

export const intlType = PropTypes.shape(({
  formatMessage: PropTypes.func
}))

export const slideTypeOptions = {
  VIDEO: 'video',
  LEFT_COLUMNS: 'leftColumns',
  DEFAULT: 'default'
}

export const statusType = PropTypes.oneOf([...Object.keys(status), 'history', 'actual'])
